﻿import { useEffect } from 'react';
import {
  Page, Card, Button, Banner, Badge, Text, DataTable,
  EmptyState, Spinner, Layout, BlockStack, InlineStack,
  ProgressBar, List, Divider, Pagination, InlineGrid,
} from '@shopify/polaris';
import { ChartLineIcon, AlertTriangleIcon, LightbulbIcon } from '@shopify/polaris-icons';
import { useMarketingAudit } from '../../hooks/useMarketingAudit';
import type { MarketingAudit, MarketingRecommendation } from '../../api/marketingAudit';
import { HealthGauge } from '../shared/HealthGauge';
import { StatCard } from '../shared/StatCard';

// ── Helpers ───────────────────────────────────────────────────────────────────

function gradeBadge(grade: string) {
  const map: Record<string, 'success' | 'info' | 'warning' | 'critical'> = {
    A: 'success', B: 'success', C: 'info', D: 'warning', F: 'critical',
  };
  return <Badge tone={map[grade] ?? 'info'}>{grade}</Badge>;
}

function severityBadge(severity: string) {
  const map: Record<string, 'critical' | 'warning' | 'info'> = {
    critical: 'critical', warning: 'warning', info: 'info',
  };
  return <Badge tone={map[severity] ?? 'info'}>{severity}</Badge>;
}

function priorityBadge(priority: string) {
  const map: Record<string, 'critical' | 'warning' | 'info'> = {
    high: 'critical', medium: 'warning', low: 'info',
  };
  return <Badge tone={map[priority] ?? 'info'}>{priority}</Badge>;
}

function ScoreBar({ label, score }: { label: string; score: number }) {
  return (
    <BlockStack gap="100">
      <InlineStack align="space-between">
        <Text as="span" variant="bodySm">{label}</Text>
        <Text as="span" variant="bodySm" fontWeight="semibold">{score}/100</Text>
      </InlineStack>
      <ProgressBar progress={score} size="small" tone={score >= 75 ? 'success' : score >= 50 ? 'primary' : 'critical'} />
    </BlockStack>
  );
}

// ── Detail View ───────────────────────────────────────────────────────────────

interface DetailProps {
  audit: MarketingAudit;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function AuditDetail({ audit, onBack, onDelete }: DetailProps) {
  const failedChecks = audit.checks.filter((c) => !c.passed);
  const passedChecks = audit.checks.filter((c) => c.passed);

  return (
    <Page fullWidth
      title="Marketing Audit Report"
      subtitle={audit.created_at ? new Date(audit.created_at).toLocaleDateString() : undefined}
      backAction={{ content: 'All Audits', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(audit.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          {/* Score overview */}
          <Card>
            <BlockStack gap="400">
              <InlineStack gap="400" align="start" blockAlign="center" wrap>
                <HealthGauge score={audit.overall_score} />
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    {gradeBadge(audit.grade)}
                    <Text as="span" variant="bodyMd" fontWeight="semibold">Marketing Readiness</Text>
                  </InlineStack>
                  <Text as="p" variant="bodyMd" tone="subdued">
                    {audit.summary_stats.passed_checks}/{audit.summary_stats.total_checks} checks passed
                    Â· {audit.summary_stats.products_analyzed} products Â· {audit.summary_stats.pages_analyzed} pages
                  </Text>
                </BlockStack>
              </InlineStack>

              <Divider />

              <BlockStack gap="300">
                <ScoreBar label="Content Marketing" score={audit.pillar_scores.content} />
                <ScoreBar label="SEO & Discovery"    score={audit.pillar_scores.seo} />
                <ScoreBar label="Product Readiness"  score={audit.pillar_scores.product} />
              </BlockStack>
            </BlockStack>
          </Card>

          {/* Issues */}
          {failedChecks.length > 0 && (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Issues Found ({failedChecks.length})</Text>
                {failedChecks.map((c) => (
                  <BlockStack key={c.check_key} gap="100">
                    <InlineStack gap="200">
                      {severityBadge(c.severity)}
                      <Text as="span" variant="bodyMd" fontWeight="semibold">{c.title}</Text>
                    </InlineStack>
                    <Text as="p" variant="bodySm" tone="subdued">{c.description}</Text>
                    <Text as="p" variant="bodySm" tone="success">{c.recommendation}</Text>
                  </BlockStack>
                ))}
              </BlockStack>
            </Card>
          )}

          {/* AI Recommendations */}
          {audit.ai_recommendations.length > 0 && (
            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">AI Marketing Recommendations</Text>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text']}
                  headings={['Priority', 'Category', 'Action', 'Expected Impact']}
                  rows={audit.ai_recommendations.map((r: MarketingRecommendation) => [
                    priorityBadge(r.priority),
                    r.category,
                    <BlockStack gap="100">
                      <Text as="p" variant="bodyMd" fontWeight="semibold">{r.title}</Text>
                      <Text as="p" variant="bodySm" tone="subdued">{r.action}</Text>
                    </BlockStack>,
                    r.expected_impact,
                  ])}
                />
              </BlockStack>
            </Card>
          )}

          {/* Passing checks */}
          {passedChecks.length > 0 && (
            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Passing Checks ({passedChecks.length})</Text>
                <List type="bullet">
                  {passedChecks.map((c) => (
                    <List.Item key={c.check_key}>
                      <Text as="span" tone="success">{c.title}</Text>
                      {' — '}
                      <Text as="span" tone="subdued">{c.description}</Text>
                    </List.Item>
                  ))}
                </List>
              </BlockStack>
            </Card>
          )}
        </Layout.Section>
      </Layout>
    </Page>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────

export function MarketingAuditPanel() {
  const {
    audits, activeAudit, loading, running, error, total, page, lastPage,
    fetchAudits, runAudit, deleteAudit, setActiveAudit, setError,
  } = useMarketingAudit();

  useEffect(() => {
    fetchAudits(1);
  }, []);

  if (activeAudit) {
    return (
      <AuditDetail
        audit={activeAudit}
        onBack={() => setActiveAudit(null)}
        onDelete={deleteAudit}
      />
    );
  }

  const latest = audits[0];
  const latestFailedCount = latest ? latest.checks.filter((c) => !c.passed).length : 0;

  return (
    <Page fullWidth
      title="Marketing Audit"
      subtitle="Analyse your store's marketing readiness across content, SEO, and product pillars"
      primaryAction={{
        content: 'Run Marketing Audit',
        loading: running,
        onAction: async () => {
          const audit = await runAudit();
          if (audit) setActiveAudit(audit);
        },
      }}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {latest && (
            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
              <StatCard label="Latest Score" value={`${latest.overall_score}/100`} icon={ChartLineIcon} iconColor="indigo" />
              <StatCard label="Issues Found" value={String(latestFailedCount)} icon={AlertTriangleIcon} iconColor="orange" />
              <StatCard label="AI Recommendations" value={String(latest.ai_recommendations.length)} icon={LightbulbIcon} iconColor="purple" />
            </InlineGrid>
          )}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Audit History ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : audits.length === 0 ? (
                <EmptyState
                  heading="No marketing audits yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{
                    content: 'Run First Audit',
                    loading: running,
                    onAction: async () => {
                      const audit = await runAudit();
                      if (audit) setActiveAudit(audit);
                    },
                  }}
                >
                  <p>
                    Click "Run Marketing Audit" to get a score across content marketing,
                    SEO, and product readiness — plus AI-generated marketing recommendations.
                  </p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['numeric', 'text', 'text', 'text', 'text', 'text']}
                  headings={['Score', 'Grade', 'Content', 'SEO', 'Product', 'Actions']}
                  rows={audits.map((a) => [
                    <Button variant="plain" onClick={() => setActiveAudit(a)}>{`${a.overall_score}/100`}</Button>,
                    gradeBadge(a.grade),
                    `${a.pillar_scores.content}/100`,
                    `${a.pillar_scores.seo}/100`,
                    `${a.pillar_scores.product}/100`,
                    <Button
                      variant="plain"
                      tone="critical"
                      size="slim"
                      onClick={() => deleteAudit(a.id)}
                    >
                      Delete
                    </Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchAudits(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchAudits(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
